/**
 * Pure helpers for the M4 lists that load their rows once and filter, sort and
 * page them in the browser (Tasks, Milestones, Projects, Contacts; T-42).
 * No React and no DOM, so they are unit-tested
 * (`src/utils/__tests__/listsModel.test.ts`).
 *
 * Server-paged lists (Leads, Quotes) do not use the filtering here: their
 * filters go to the API and their counts come from the server.
 */
import type { SortDir, SortState } from '../table/tableModel';

/* ------------------------------------------------------------------ */
/* Sort in the URL: `sort=due_date` (asc) · `sort=-due_date` (desc)   */
/* ------------------------------------------------------------------ */

/** URL pattern for a sort param (a column key with an optional leading "-"). */
export const SORT_PATTERN = /^-?[a-z][a-z_]*$/;

export function parseSort(raw: string, allowed: readonly string[]): SortState | null {
  if (!raw || !SORT_PATTERN.test(raw)) return null;
  const dir: SortDir = raw.startsWith('-') ? 'desc' : 'asc';
  const key = dir === 'desc' ? raw.slice(1) : raw;
  return allowed.includes(key) ? { key, dir } : null;
}

export function serializeSort(sort: SortState | null | undefined): string {
  if (!sort) return '';
  return sort.dir === 'desc' ? `-${sort.key}` : sort.key;
}

export type SortValue = string | number | null | undefined;

/**
 * Stable sort by one column. Empty values (null, undefined, '') always go
 * last, in both directions, so "by due date" never starts with rows that have
 * no date. Text compares with the Hebrew collation (numbers inside text
 * compare as numbers).
 */
export function sortRows<T>(rows: readonly T[], sort: SortState | null | undefined, accessors: Record<string, (row: T) => SortValue>): T[] {
  if (!sort) return [...rows];
  const get = accessors[sort.key];
  if (!get) return [...rows];
  const collator = new Intl.Collator('he', { numeric: true, sensitivity: 'base' });
  const sign = sort.dir === 'asc' ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index, value: get(row) }))
    .sort((a, b) => {
      const ea = a.value === null || a.value === undefined || a.value === '';
      const eb = b.value === null || b.value === undefined || b.value === '';
      if (ea || eb) return ea === eb ? a.index - b.index : ea ? 1 : -1;
      const cmp = typeof a.value === 'number' && typeof b.value === 'number'
        ? a.value - b.value
        : collator.compare(String(a.value), String(b.value));
      return cmp === 0 ? a.index - b.index : cmp * sign;
    })
    .map((x) => x.row);
}

/* ------------------------------------------------------------------ */
/* Client-side pages                                                   */
/* ------------------------------------------------------------------ */

export interface PageSlice<T> {
  rows: T[];
  /** 1-based, clamped into range. */
  page: number;
  pageCount: number;
  total: number;
}

export function pageSlice<T>(rows: readonly T[], page: number, size: number): PageSlice<T> {
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / size));
  const current = Math.min(Math.max(1, Math.trunc(page) || 1), pageCount);
  const start = (current - 1) * size;
  return { rows: rows.slice(start, start + size), page: current, pageCount, total };
}

/* ------------------------------------------------------------------ */
/* Filter options taken from the rows themselves                       */
/* ------------------------------------------------------------------ */

export interface Option {
  value: string;
  label: string;
}

/** Distinct non-empty values, sorted by label (Hebrew collation). */
export function distinctOptions<T>(rows: readonly T[], value: (row: T) => string | null | undefined, label?: (row: T) => string | null | undefined): Option[] {
  const seen = new Map<string, string>();
  for (const row of rows) {
    const v = value(row);
    if (!v || seen.has(v)) continue;
    seen.set(v, (label ? label(row) : v) || v);
  }
  const collator = new Intl.Collator('he', { numeric: true, sensitivity: 'base' });
  return [...seen].map(([v, l]) => ({ value: v, label: l })).sort((a, b) => collator.compare(a.label, b.label));
}

/**
 * Options merged by label: two values that read the same (the legacy task
 * status `open` and `in_progress` are both "בביצוע") become ONE option whose
 * value lists both ("in_progress,open"), so the user never sees two identical
 * pills. Use with `matchesAny`.
 */
export function mergeByLabel(options: readonly Option[]): Option[] {
  const byLabel = new Map<string, string[]>();
  for (const o of options) byLabel.set(o.label, [...(byLabel.get(o.label) ?? []), o.value]);
  return [...byLabel].map(([label, values]) => ({ value: [...values].sort().join(','), label }));
}

/** A filter value that may list several values ("a,b"). Empty matches everything. */
export function matchesAny(filter: string, value: string | null | undefined): boolean {
  if (!filter) return true;
  return !!value && filter.split(',').includes(value);
}

/** Keep the selected value as an option even when no row carries it any more (a stale URL). */
export function withSelected(options: Option[], selected: string, label?: string): Option[] {
  if (!selected || options.some((o) => o.value === selected)) return options;
  return [{ value: selected, label: label || selected }, ...options];
}

/* ------------------------------------------------------------------ */
/* Dates                                                               */
/* ------------------------------------------------------------------ */

/** `YYYY-MM-DD` of a date in local time. */
export function isoDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The calendar day of an API date (`2026-10-07` or a full ISO timestamp), or '' when invalid. */
export function dayOf(value: string | null | undefined): string {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : isoDay(d);
}

/** Inclusive day range; an empty bound is open. A row without a date is outside any range. */
export function inDayRange(value: string | null | undefined, from: string, to: string): boolean {
  if (!from && !to) return true;
  const day = dayOf(value);
  if (!day) return false;
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

/** The due day has passed and the item is still open. */
export function isOverdue(due: string | null | undefined, done: boolean, today: Date = new Date()): boolean {
  if (done) return false;
  const day = dayOf(due);
  return !!day && day < isoDay(today);
}

/* ------------------------------------------------------------------ */
/* Tasks (/tasks)                                                      */
/* ------------------------------------------------------------------ */

export interface TaskLike {
  title: string;
  status: string;
  project_name?: string | null;
  organization_name?: string | null;
  agent_name?: string | null;
  start_date?: string;
  due_date?: string;
  cancelled_at?: string;
}

export interface TaskFilters {
  q: string;
  status: string;
  /** Customer name (the rows carry the name only). */
  customer: string;
  /** Worker name (the rows carry the main assignee's name only). */
  worker: string;
  /** `overdue`: due day passed, not completed, not cancelled. */
  due: string;
  /** Inclusive range on the start date (or the due date when there is no start). */
  from: string;
  to: string;
}

/** Finished: completed (or the legacy `closed`, still on older tasks) or cancelled. */
export const isTaskDone = (task: Pick<TaskLike, 'status' | 'cancelled_at'>): boolean =>
  task.status === 'completed' || task.status === 'closed' || !!task.cancelled_at;

/** Same rules as the pre-v2 page (search in the title, dates on start || due), plus `due=overdue`. */
export function filterTasks<T extends TaskLike>(rows: readonly T[], f: TaskFilters, today: Date = new Date()): T[] {
  const q = f.q.trim().toLocaleLowerCase();
  return rows.filter((task) => {
    if (q && !task.title.toLocaleLowerCase().includes(q)) return false;
    if (!matchesAny(f.status, task.status)) return false;
    if (f.customer && task.organization_name !== f.customer) return false;
    if (f.worker && task.agent_name !== f.worker) return false;
    if (f.due === 'overdue' && !isOverdue(task.due_date, isTaskDone(task), today)) return false;
    if ((f.from || f.to) && !inDayRange(task.start_date || task.due_date, f.from, f.to)) return false;
    return true;
  });
}

/* ------------------------------------------------------------------ */
/* Projects (/projects)                                                */
/* ------------------------------------------------------------------ */

export interface ProjectLike {
  name: string;
  status: string;
  organization_id?: string;
  organization_name?: string | null;
  overdue_days?: number | null;
  /** P-04/D */
  project_manager_id?: string | null;
  project_manager_name?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

/** P-04/D — the statuses the status filter offers one by one. */
export const PROJECT_FILTER_STATUSES = ['planning', 'active', 'on_hold'] as const;

/**
 * The pre-v2 tabs, now the views: "open" = everything but completed; "closed" = completed.
 * P-02c/F-07 — "cancelled" = the cancelled projects (the server sends them only on request).
 * P-04/D — one status (planning / active / on_hold).
 */
export function projectInState(project: Pick<ProjectLike, 'status'>, state: string): boolean {
  if (state === 'cancelled') return project.status === 'cancelled';
  if ((PROJECT_FILTER_STATUSES as readonly string[]).includes(state)) return project.status === state;
  return state === 'closed' ? project.status === 'completed' : project.status !== 'completed';
}

/** The server sends `overdue_days` = 0 for an open project whose end date is still ahead: only > 0 is late. */
export function isProjectOverdue(p: Pick<ProjectLike, 'overdue_days'>): boolean {
  return typeof p.overdue_days === 'number' && p.overdue_days > 0;
}

/** P-04/D — the project's dates overlap the range (inclusive days; an open side is unbounded). */
export function projectInDateRange(p: Pick<ProjectLike, 'start_date' | 'end_date'>, from: string, to: string): boolean {
  if (!from && !to) return true;
  const start = (p.start_date ?? '').slice(0, 10);
  const end = (p.end_date ?? '').slice(0, 10) || start;
  if (!start && !end) return false;
  if (to && start && start > to) return false;
  if (from && end && end < from) return false;
  return true;
}

export function filterProjects<T extends ProjectLike>(
  rows: readonly T[],
  f: { q: string; state: string; customer: string; overdue: boolean; manager?: string; from?: string; to?: string },
): T[] {
  const q = f.q.trim().toLocaleLowerCase();
  return rows.filter((p) => {
    if (!projectInState(p, f.state)) return false;
    if (f.customer && p.organization_id !== f.customer) return false;
    if (f.manager && p.project_manager_id !== f.manager) return false;
    if (f.overdue && !isProjectOverdue(p)) return false;
    if (!projectInDateRange(p, f.from ?? '', f.to ?? '')) return false;
    // P-04/D — the search: project name, customer or project manager.
    if (q && !`${p.name} ${p.organization_name ?? ''} ${p.project_manager_name ?? ''}`.toLocaleLowerCase().includes(q)) return false;
    return true;
  });
}
